/* React */
import React, { useCallback, useEffect } from "react";

/* React-Redux */
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  clearOptions,
  setOptions,
} from "src/redux/features/insights-filter/insightsFilterOptionsSlice";

/* Thunks */
import { fetchDescriptiveStatistics } from "src/redux/thunks/descriptive-statistics/descriptiveStatisticsThunk";

/* Utils */
import useChartLoadingScreen from "src/utils/hooks/useChartLoadingScreen";
import useFilterCleanup from "src/utils/hooks/useFilterCleanup";
import ChartLayoutWrapper from "src/utils/wrapper/chart-layout-wrapper/ChartLayoutWrapper";
import { useFetchWithMainCondition } from "src/utils/hooks/useFetchWithMainCondition";
import { useEffectiveShowNoValueEntries } from "src/utils/hooks/useEffectiveShowNoValueEntries";

/* Layout */
import { getStackedLayout } from "src/utils/layout/stacked-layout/getStackedLayout";

/* Constants */
import { ChartType } from "src/shared/constants/charts/Chart";

/* Interfaces */
import { BaseParams } from "src/shared/interfaces/charts/FetchWithMainCondition";

const DescriptiveStatistics: React.FC = () => {
  const dispatch = useDispatch();
  useFilterCleanup();

  const insightsFilterState = useSelector(
    (state: RootState) => state.insightsFilter
  );

  const { features } = insightsFilterState;

  const { mainCondition } = insightsFilterState;

  const descriptiveStatisticsState = useSelector(
    (state: RootState) => state.descriptiveStatistics
  );

  const { data, isLoading, modifiedAt, error } =
    descriptiveStatisticsState;

  const customCohortsState = useSelector(
    (state: RootState) => state.customCohorts
  );

  // A custom cohort has no well-defined completeness denominator, so the toggle
  // is disabled there and no-value entries are hidden regardless of a stale
  // slice value (see `useEffectiveShowNoValueEntries`).
  const showNoValueEntries = useEffectiveShowNoValueEntries();

  useChartLoadingScreen(isLoading, modifiedAt);

  const buildParams = useCallback(
    (baseParams: BaseParams) => ({
      ...baseParams,
      feature: insightsFilterState.features.value,
    }),
    [insightsFilterState.features.value]
  );

  useFetchWithMainCondition({
    mainCondition,
    debouncedValue: insightsFilterState,
    customCohortsState,
    fetchAction: fetchDescriptiveStatistics,
    buildParams,
  });

  const tmpTypes = data?.types ?? [];

  useEffect(() => {
    if (data?.types.includes(ChartType.BAR)) {
      dispatch(setOptions(ChartType.BAR));
    } else if (data?.types.includes(ChartType.HISTOGRAM)) {
      dispatch(setOptions(ChartType.HISTOGRAM));
    } else {
      dispatch(clearOptions());
    }
    return () => {
      dispatch(clearOptions());
    };
  }, [dispatch, data?.types]);

  const layout = getStackedLayout(
    tmpTypes,
    data!,
    features.value,
    showNoValueEntries
  );

  return (
    <ChartLayoutWrapper layout={layout} error={error} />
  );
};

export default DescriptiveStatistics;
